// client/src/components/ui/text-reveal-card.jsx
import React, { useEffect, useRef, useState, memo } from "react";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";
import { cn } from "../lib/utils";

export const TextRevealCard = ({ text, revealText, children, className }) => {
  const [widthPercentage, setWidthPercentage] = useState(0);
  const cardRef = useRef(null);
  const textRef = useRef(null);
  const [dimensions, setDimensions] = useState({ left: 0, width: 0 });
  const [textHeight, setTextHeight] = useState(0);
  const [isMouseOver, setIsMouseOver] = useState(false);

  // Update dimensions and text height on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (cardRef.current) {
        const { left, width } = cardRef.current.getBoundingClientRect();
        setDimensions({ left, width });
      }
      if (textRef.current) {
        const { height } = textRef.current.getBoundingClientRect();
        setTextHeight(height);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  function mouseMoveHandler(event) {
    event.preventDefault();
    const { clientX } = event;
    if (cardRef.current) {
      const relativeX = clientX - dimensions.left;
      setWidthPercentage((relativeX / dimensions.width) * 100);
    }
  }

  function mouseLeaveHandler() {
    setIsMouseOver(false);
    setWidthPercentage(0);
  }

  function mouseEnterHandler() {
    setIsMouseOver(true);
  }

  function touchMoveHandler(event) {
    event.preventDefault();
    const clientX = event.touches[0].clientX;
    if (cardRef.current) {
      const relativeX = clientX - dimensions.left;
      setWidthPercentage((relativeX / dimensions.width) * 100);
    }
  }

  const rotateDeg = (widthPercentage - 50) * 0.1;

  // Calculate dynamic minimum height with additional padding
  const dynamicMinHeight = textHeight > 0 ? `${textHeight + 40}px` : "120px";

  return (
    <div
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
      onMouseMove={mouseMoveHandler}
      onTouchStart={mouseEnterHandler}
      onTouchEnd={mouseLeaveHandler}
      onTouchMove={touchMoveHandler}
      ref={cardRef}
      className={cn(
        "bg-[#1d1c20] border border-white/[0.08] w-full md:w-[50rem] lg:w-[60rem] rounded-lg p-8 relative overflow-hidden",
        className
      )}
    >
      {children}
      <div
        className="relative flex items-center justify-center"
        style={{
          minHeight: dynamicMinHeight,
          paddingTop: "1.5rem",
          paddingBottom: "1.5rem",
        }}
      >
        <motion.div
          style={{ width: "100%" }}
          animate={
            isMouseOver
              ? { opacity: widthPercentage > 0 ? 1 : 0, clipPath: `inset(0 ${100 - widthPercentage}% 0 0)` }
              : { clipPath: `inset(0 ${100 - widthPercentage}% 0 0)` }
          }
          transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
          className="absolute bg-[#1d1c20] z-20 will-change-transform"
        >
          <p
            ref={textRef}
            style={{ textShadow: "4px 4px 15px rgba(0,0,0,0.5)" }}
            className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-b from-white to-neutral-300 whitespace-normal text-center px-4"
          >
            {revealText}
          </p>
        </motion.div>
        <motion.div
          animate={{
            left: `${widthPercentage}%`,
            rotate: `${rotateDeg}deg`,
            opacity: widthPercentage > 0 ? 1 : 0,
          }}
          transition={isMouseOver ? { duration: 0 } : { duration: 0.4 }}
          className="w-[8px] bg-gradient-to-b from-transparent via-neutral-800 to-transparent absolute z-50 will-change-transform"
          style={{ minHeight: dynamicMinHeight }}
        ></motion.div>
        <div className="overflow-visible">
          <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-bold bg-clip-text text-transparent bg-[#323238] whitespace-normal text-center px-4">
            {text}
          </p>
          <MemoizedStars />
        </div>
      </div>
    </div>
  );
};

export const TextRevealCardTitle = ({ children, className }) => {
  return <h2 className={twMerge("text-white text-lg mb-2", className)}>{children}</h2>;
};

export const TextRevealCardDescription = ({ children, className }) => {
  return <p className={twMerge("text-[#a9a9a9] text-sm", className)}>{children}</p>;
};

const Stars = () => {
  const randomMove = () => Math.random() * 4 - 2;
  const randomOpacity = () => Math.random();
  const random = () => Math.random();

  return (
    <div className="absolute inset-0">
      {[...Array(80)].map((_, i) => (
        <motion.span
          key={`star-${i}`}
          animate={{
            top: `calc(${random() * 100}% + ${randomMove()}px)`,
            left: `calc(${random() * 100}% + ${randomMove()}px)`,
            opacity: randomOpacity(),
            scale: [1, 1.2, 0],
          }}
          transition={{
            duration: random() * 10 + 20,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: "absolute",
            top: `${random() * 100}%`,
            left: `${random() * 100}%`,
            width: `2px`,
            height: `2px`,
            backgroundColor: "white",
            borderRadius: "50%",
            zIndex: 1,
          }}
          className="inline-block"
        ></motion.span>
      ))}
    </div>
  );
};

export const MemoizedStars = memo(Stars);
